// =============================================
// profileApi — self-service writes to the caller's own users row
// =============================================
// Drop-in replacement for dataAccess.saveRow. When the write targets the `users`
// table for the CURRENT session user, it routes to PUT /api/auth/profile (which any
// authenticated user may call — the generic /api/db CRUD is admin-only). Every other
// table/row, and the offline/no-token case, delegates to dataAccess.saveRow.
//
// This is what makes the Membership plan-switch (users.plan_id) and Settings → My
// Profile edits work for account owners, not just admins.
// =============================================
import { saveRow as dbSaveRow, getTableData, saveTableData, checkApi } from '../features/db-viewer/data/dataAccess';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function authToken(): string | null {
  return localStorage.getItem('akt_auth_token');
}

function sessionUserId(): number | null {
  try {
    const raw = localStorage.getItem('akt_session_user');
    if (!raw) return null;
    const id = JSON.parse(raw).id;
    return id != null ? Number(id) : null;
  } catch { return null; }
}

/**
 * Merge the server's returned row into the local users cache so getTableData() is
 * fresh. Upserts — an account owner's own row often isn't in the cache (the generic
 * users load is admin-only), so we add it from the returned row (which is RETURNING *).
 */
function patchUsersCache(id: number | string, saved: Record<string, any>): void {
  try {
    const data = getTableData('users');
    // eslint-disable-next-line eqeqeq
    const i = data.findIndex(r => r.user_id == id);
    if (i >= 0) data[i] = { ...data[i], ...saved };
    else data.push(saved);
    saveTableData('users', data);
  } catch { /* best effort */ }
}

/**
 * Warm the caller's own users row into the local cache from /api/auth/me-row, so
 * features that read `users` via getTableData (plan/caps/current-plan) work for
 * account owners — who can't read the admin-only /api/db users table. No-op offline.
 */
export async function warmMyProfile(): Promise<void> {
  const id = sessionUserId();
  if (id == null || !authToken() || !(await checkApi())) return;
  try {
    const res = await fetch(`${API_BASE}/auth/me-row`, { headers: { Authorization: `Bearer ${authToken()}` } });
    if (res.ok) patchUsersCache(id, await res.json());
  } catch { /* best effort — offline */ }
}

export async function saveRow(
  tableName: string,
  id: number | string,
  updates: Record<string, any>,
  pkColumn?: string,
): Promise<Record<string, any>> {
  const isSelfUser = tableName === 'users' && sessionUserId() != null && String(id) === String(sessionUserId());
  if (isSelfUser && authToken() && (await checkApi())) {
    try {
      const res = await fetch(`${API_BASE}/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
        body: JSON.stringify(updates),
      });
      if (res.ok) {
        const saved = await res.json();
        patchUsersCache(id, saved);
        return saved;
      }
      // Real server error — surface it (don't silently fall back to admin-only path).
      const body = await res.json().catch(() => ({} as any));
      const e = new Error(body.message || `Profile update ${res.status}`);
      (e as any).status = res.status;
      throw e;
    } catch (err: any) {
      if (err && typeof err.status === 'number') throw err;
      // Network error → fall through to the local/dataAccess path (offline support).
    }
  }
  return dbSaveRow(tableName, id, updates, pkColumn);
}
